import React, { useEffect, useState } from "react";
import { BeforeAfter } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import ImageUpload from "@/components/ImageUpload";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import LivePreviewPane from "@/components/admin/LivePreviewPane";
import BeforeAfterPreview from "@/components/admin/BeforeAfterPreview";
import { notifyPreviewRefresh } from "@/components/admin/GlobalPreviewPane";

const empty = { title: "", description: "", before_url: "", after_url: "", is_active: true, sort_order: 0 };

const GalleryManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems(await BeforeAfter.list()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEdit("new"); setForm(empty); };
  const openEdit = (b) => { setEdit(b.id); setForm({ ...empty, ...b }); };
  const close = () => { setEdit(null); setForm(empty); };

  const save = async ({ keepOpen = false } = {}) => {
    if (!form.title || !form.before_url || !form.after_url) {
      toast.error("Title and both image URLs are required");
      return;
    }
    setSaving(true);
    try {
      let saved;
      if (edit === "new") saved = await BeforeAfter.create(form);
      else saved = await BeforeAfter.update(edit, form);
      toast.success("Saved");
      notifyPreviewRefresh();
      if (keepOpen) {
        if (saved && saved.id) {
          setEdit(saved.id);
          setForm({ ...empty, ...saved });
        }
        load();
      } else {
        close();
        load();
      }
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}"?`)) return;
    try { await BeforeAfter.remove(b.id); toast.success("Deleted"); notifyPreviewRefresh(); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="p-10" data-testid="gallery-manager">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-atc-blue mb-2">Gallery</div>
          <h1 className="font-display font-black text-4xl text-white">Before & After</h1>
          <p className="text-slate-400 text-sm mt-2">Paste image URLs (use a hosted upload service or any image URL).</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full brand-gradient text-white text-sm font-semibold" data-testid="new-ba-button">
          <Plus className="h-4 w-4" /> New Pair
        </button>
      </div>

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="glass rounded-xl p-10 text-center text-slate-400">No before/after pairs yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {items.map((b) => (
            <div key={b.id} className="glass rounded-2xl p-5" data-testid={`ba-card-${b.id}`}>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="aspect-[4/3] rounded-lg bg-cover bg-center border border-white/10" style={{ backgroundImage: `url(${b.before_url})` }}>
                  <div className="px-2 py-0.5 m-2 inline-block rounded-full bg-rose-500/80 text-white text-[10px] uppercase tracking-wider">Before</div>
                </div>
                <div className="aspect-[4/3] rounded-lg bg-cover bg-center border border-white/10" style={{ backgroundImage: `url(${b.after_url})` }}>
                  <div className="px-2 py-0.5 m-2 inline-block rounded-full bg-atc-green/90 text-slate-950 text-[10px] uppercase tracking-wider">After</div>
                </div>
              </div>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="font-display font-bold text-white">{b.title}</div>
                  {b.description && <div className="text-xs text-slate-400 mt-1">{b.description}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => openEdit(b)} className="text-slate-300 hover:text-white" data-testid={`edit-ba-${b.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(b)} className="text-slate-300 hover:text-rose-400" data-testid={`delete-ba-${b.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="mt-3">
                <span className={`text-xs px-2 py-0.5 rounded-full uppercase tracking-wider ${b.is_active ? "bg-atc-green/15 text-atc-green" : "bg-slate-700/30 text-slate-400"}`}>
                  {b.is_active ? "Live" : "Hidden"}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && close()}>
        <DialogContent className="bg-slate-950 border-white/10 text-white max-w-[1200px] w-[95vw] max-h-[92vh] overflow-hidden p-0 flex flex-col">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-white/5 shrink-0">
            <DialogTitle>{edit === "new" ? "New before/after" : "Edit before/after"}</DialogTitle>
          </DialogHeader>
          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-5 px-6 py-5 overflow-y-auto flex-1">
          <div className="space-y-4 min-w-0">
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-400">Title</label>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="bg-slate-950 border-white/10 mt-1" data-testid="ba-title" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-400">Description (optional)</label>
              <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} className="bg-slate-950 border-white/10 mt-1" data-testid="ba-description" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-400 block mb-2">Before image</label>
              <ImageUpload
                value={form.before_url}
                onChange={(url) => setForm((f) => ({ ...f, before_url: url }))}
                testId="ba-before"
                label="Before photo"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-400 block mb-2">After image</label>
              <ImageUpload
                value={form.after_url}
                onChange={(url) => setForm((f) => ({ ...f, after_url: url }))}
                testId="ba-after"
                label="After photo"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3 items-end">
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-slate-400">Sort</label>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))} className="bg-slate-950 border-white/10 mt-1" data-testid="ba-sort" />
              </div>
              <div className="flex items-center gap-2 pb-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} data-testid="ba-active" />
                <span className="text-sm text-slate-300">Active</span>
              </div>
            </div>
          </div>

          <div className="hidden lg:block sticky top-0 self-start h-[calc(92vh-9rem)]">
            <LivePreviewPane title="Live preview · Homepage slider" testId="ba-live-preview">
              <BeforeAfterPreview ba={form} />
            </LivePreviewPane>
          </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-white/5 shrink-0">
            <button onClick={close} className="px-4 py-2 rounded-full text-sm text-slate-300 hover:text-white">Cancel</button>
            <button onClick={() => save({ keepOpen: true })} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium text-slate-200 border border-white/15 hover:bg-slate-900/50 disabled:opacity-60" data-testid="ba-save-keep">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save & keep editing
            </button>
            <button onClick={() => save()} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2 rounded-full brand-gradient text-white text-sm font-semibold disabled:opacity-60" data-testid="ba-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save & close
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryManager;
